import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { reservations as resApi, ref as refApi, projects as projApi, addWithDupGuard } from "../services/api";
import PhoneInput from "../components/PhoneInput"; // v3.3.749 — إدخال جوال موحّد
import toast from "react-hot-toast";

// ═══════════════════════════════════════════════════════════════
// MobilePage — واجهة الجوال المبسطة
// ═══════════════════════════════════════════════════════════════

const C = {
  bg: "#0f172a", card: "#1e293b", border: "#334155",
  text: "#f1f5f9", sub: "#94a3b8", hint: "#64748b",
  green: "#128C7E", greenLight: "#4ade80", greenDark: "#052e16",
  red: "#f87171", redDark: "#450a0a",
  blue: "#60a5fa", blueDark: "#1e3a5f",
  yellow: "#fbbf24", yellowDark: "#422006",
  purple: "#a78bfa",
};

const inputS = {
  width: "100%", boxSizing: "border-box", padding: "11px 12px",
  background: C.card, border: `0.5px solid ${C.border}`, borderRadius: 8,
  color: C.text, fontFamily: "inherit", fontSize: 13, outline: "none", direction: "rtl",
};
const selectS = { ...inputS, appearance: "none", color: C.sub };
const labelS = { fontSize: 11, color: C.sub, display: "block", marginBottom: 5, fontWeight: 500 };
const btnAdd = {
  width: 42, height: 42, borderRadius: 8, background: C.card,
  border: `0.5px solid ${C.green}`, display: "flex", alignItems: "center",
  justifyContent: "center", flexShrink: 0, cursor: "pointer",
};

const fmt = n => Number(n || 0).toLocaleString("en-US");

// ─── Header ─────────────────────────────────────────────────
function MobileHeader({ title, subtitle, onBack, color }) {
  return (
    <div style={{ background: color || C.card, padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, direction: "rtl", borderBottom: `0.5px solid ${C.border}` }}>
      {onBack && (
        <div onClick={onBack} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(0,0,0,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, cursor: "pointer" }}>›</div>
      )}
      <div style={{ flex: 1, textAlign: "center" }}>
        <div style={{ fontSize: 16, fontWeight: 500, color: "#fff" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>{subtitle}</div>}
      </div>
      <div style={{ width: 28 }} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// HomeScreen — الشاشة الرئيسية
// ═══════════════════════════════════════════════════════════════
function HomeScreen({ onNavigate, stats, statsLoading, user, statsFilter, setStatsFilter }) {
  const monthNames = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
  const currentMonth = monthNames[new Date().getMonth()];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, direction: "rtl" }}>
      <div style={{ background: `linear-gradient(135deg, ${C.green}, #075E54)`, padding: "20px 16px 16px", textAlign: "center" }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>نظام ERP لإدارة الشركة</div>
        <div style={{ fontSize: 18, fontWeight: 500, color: "#fff" }}>رئال للتطوير العقاري</div>
        <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.15)", padding: "4px 12px", borderRadius: 20 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.greenLight }} />
          <span style={{ fontSize: 12, color: "#d1fae5" }}>{user?.name || user?.username}</span>
        </div>
      </div>

      <div style={{ padding: "20px 16px" }}>

        {/* فلتر الشهر / الكل */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14, justifyContent: "center" }}>
          <span onClick={() => setStatsFilter("month")} style={{
            padding: "7px 20px", borderRadius: 20, fontSize: 13, cursor: "pointer", fontWeight: 500,
            background: statsFilter === "month" ? C.green : C.card, color: statsFilter === "month" ? "#fff" : C.sub,
            border: statsFilter === "month" ? "none" : `0.5px solid ${C.border}`,
          }}>{currentMonth} {new Date().getFullYear()}</span>
          <span onClick={() => setStatsFilter("all")} style={{
            padding: "7px 20px", borderRadius: 20, fontSize: 13, cursor: "pointer", fontWeight: 500,
            background: statsFilter === "all" ? C.green : C.card, color: statsFilter === "all" ? "#fff" : C.sub,
            border: statsFilter === "all" ? "none" : `0.5px solid ${C.border}`,
          }}>الكل</span>
        </div>

        {/* الإحصائيات */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          <div style={{ background: C.card, borderRadius: 12, padding: "16px 10px", textAlign: "center", border: `0.5px solid ${C.border}` }}>
            <div style={{ fontSize: 26, fontWeight: 500, color: C.text }}>{statsLoading ? "..." : fmt(stats.total)}</div>
            <div style={{ fontSize: 11, color: C.sub, marginTop: 4 }}>إجمالي</div>
          </div>
          <div style={{ background: C.card, borderRadius: 12, padding: "16px 10px", textAlign: "center", border: `0.5px solid ${C.border}` }}>
            <div style={{ fontSize: 26, fontWeight: 500, color: C.greenLight }}>{statsLoading ? "..." : fmt(stats.completed)}</div>
            <div style={{ fontSize: 11, color: C.sub, marginTop: 4 }}>مكتمل</div>
          </div>
          <div style={{ background: C.card, borderRadius: 12, padding: "16px 10px", textAlign: "center", border: `0.5px solid ${C.border}` }}>
            <div style={{ fontSize: 26, fontWeight: 500, color: C.red }}>{statsLoading ? "..." : fmt(stats.failed)}</div>
            <div style={{ fontSize: 11, color: C.sub, marginTop: 4 }}>لم تتم</div>
          </div>
        </div>

        <div style={{ fontSize: 11, color: C.hint, marginBottom: 8, fontWeight: 500 }}>الإجراءات السريعة</div>

        <div onClick={() => onNavigate("new")} style={{ background: C.green, borderRadius: 12, padding: 16, marginBottom: 10, display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "#fff", flexShrink: 0 }}>+</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "#fff" }}>حجز جديد</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>إضافة حجز بالبيانات الأساسية</div>
          </div>
        </div>

        {[
          { key: "reservations", icon: "📋", label: "الحجوزات", desc: "استعراض وبحث في كل الحجوزات", color: "#1e3a5f" },
          { key: "projects", icon: "🏢", label: "المشاريع", desc: "خريطة الوحدات والحالات", color: "#1a3326" },
        ].map(item => (
          <div key={item.key} onClick={() => onNavigate(item.key)} style={{ background: C.card, borderRadius: 12, padding: 16, marginBottom: 10, display: "flex", alignItems: "center", gap: 12, border: `0.5px solid ${C.border}`, cursor: "pointer" }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: item.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{item.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: C.text }}>{item.label}</div>
              <div style={{ fontSize: 11, color: C.sub }}>{item.desc}</div>
            </div>
            <div style={{ color: C.hint, fontSize: 14 }}>‹</div>
          </div>
        ))}
      </div>

      <div style={{ padding: "12px 16px", borderTop: `0.5px solid ${C.card}`, textAlign: "center" }}>
        <span style={{ fontSize: 10, color: C.hint }}>وضع الجوال</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ReservationsList — قائمة الحجوزات
// ═══════════════════════════════════════════════════════════════
function ReservationsList({ onBack }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | completed | failed

  useEffect(() => {
    resApi.list({ limit: 200 }).then(r => { setItems(r.data.reservations || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = items.filter(r => {
    if (filter === "completed" && r.operation_status !== "تمت العمليه") return false;
    if (filter === "failed" && r.operation_status !== "لم تتم العمليه") return false;
    if (search) {
      const q = search.toLowerCase();
      return String(r.id).includes(q) || (r.client_name || "").toLowerCase().includes(q) || (r.linked_clients || []).some(c => c.name.toLowerCase().includes(q));
    }
    return true;
  });

  const statusColor = s => s === "تمت العمليه" ? C.greenLight : s === "لم تتم العمليه" ? C.red : C.yellow;
  const statusBg = s => s === "تمت العمليه" ? "#14532d" : s === "لم تتم العمليه" ? "#450a0a" : "#422006";

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      <MobileHeader title="الحجوزات" onBack={onBack} />
      <div style={{ padding: "12px 16px", direction: "rtl" }}>
        <div style={{ position: "relative", marginBottom: 12 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="بحث برقم الحجز أو اسم العميل..." style={{ ...inputS, paddingLeft: 36 }} />
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.hint, fontSize: 14 }}>🔍</span>
        </div>
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 12 }}>
          {[
            { key: "all", label: `الكل ${items.length}` },
            { key: "completed", label: `مكتمل ${items.filter(r => r.operation_status === "تمت العمليه").length}` },
            { key: "failed", label: `لم تتم ${items.filter(r => r.operation_status === "لم تتم العمليه").length}` },
          ].map(f => (
            <span key={f.key} onClick={() => setFilter(f.key)} style={{
              flexShrink: 0, padding: "6px 14px", borderRadius: 20, fontSize: 12, cursor: "pointer", fontWeight: 500,
              background: filter === f.key ? C.green : C.card, color: filter === f.key ? "#fff" : C.sub,
              border: filter === f.key ? "none" : `0.5px solid ${C.border}`,
            }}>{f.label}</span>
          ))}
        </div>
      </div>

      <div style={{ padding: "0 16px 16px", direction: "rtl", display: "flex", flexDirection: "column", gap: 8 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: C.sub }}>جاري التحميل...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: C.sub }}>لا توجد نتائج</div>
        ) : filtered.slice(0, 50).map(r => (
          <div key={r.id} style={{ background: C.card, borderRadius: 10, padding: 14, border: `0.5px solid ${C.border}`, borderRight: `3px solid ${statusColor(r.operation_status)}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 500, color: C.text }}>#{r.id}</span>
              <span style={{ fontSize: 10, padding: "3px 8px", background: statusBg(r.operation_status), color: statusColor(r.operation_status), borderRadius: 6 }}>{r.operation_status || "—"}</span>
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: C.sub, marginBottom: 6 }}>
              <span>{r.date?.slice(0, 10)}</span>
              <span style={{ color: C.blue }}>{r.operation_type}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
              <span style={{ color: "#cbd5e1" }}>{r.linked_clients?.map(c => c.name).join("، ") || r.client_name || "—"}</span>
              <span style={{ color: C.yellow, fontWeight: 500 }}>{fmt(r.value)} ر.س</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ProjectsList — قائمة المشاريع ثم خريطة الوحدات
// ═══════════════════════════════════════════════════════════════
function ProjectsScreen({ onBack }) {
  const [projectsList, setProjectsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    projApi.list().then(r => { setProjectsList(r.data.projects || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (selectedProject) {
    return <ProjectUnitMap project={selectedProject} onBack={() => setSelectedProject(null)} />;
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      <MobileHeader title="المشاريع" onBack={onBack} />
      <div style={{ padding: "16px", direction: "rtl", display: "flex", flexDirection: "column", gap: 10 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: C.sub }}>جاري التحميل...</div>
        ) : projectsList.map(p => (
          <div key={p.id} onClick={() => setSelectedProject(p)} style={{ background: C.card, borderRadius: 10, padding: 14, border: `0.5px solid ${C.border}`, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#1a3326", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🏢</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{p.name}</div>
              <div style={{ fontSize: 11, color: C.sub }}>{p.developer_name || "—"}</div>
            </div>
            <div style={{ color: C.hint, fontSize: 14 }}>‹</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Project Unit Map ───────────────────────────────────────
function ProjectUnitMap({ project, onBack }) {
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBuilding, setSelectedBuilding] = useState("all");
  // خيارات العرض
  const [showArea, setShowArea] = useState(false);
  const [showPrice, setShowPrice] = useState(false);
  const [showRooms, setShowRooms] = useState(false);
  // v3.3.335 — خيارات عرض إضافية
  const [showOfficialPrice, setShowOfficialPrice] = useState(false);
  const [showSalePrice, setShowSalePrice] = useState(false);
  const [showClientName, setShowClientName] = useState(false);
  const [showMarketer, setShowMarketer] = useState(false);
  const [showFacade, setShowFacade] = useState(false);
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [colorAll, setColorAll] = useState(true); // true = ملونة حسب الحالة، false = المتاح فقط ملون والباقي رمادي
  const [hiddenFromReport, setHiddenFromReport] = useState(new Set()); // وحدات مخفية من آخر تقرير توفر
  const [lastReport, setLastReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  // v3.3.336 — خيارات التقرير (مثل تبويب تسويق المشروع)
  const [showReportOptions, setShowReportOptions] = useState(false);
  const [reportStatuses, setReportStatuses] = useState(["available", "resale"]); // v3.4.153 — المتاح + إعادة البيع افتراضيًا
  const [reportSections, setReportSections] = useState({ includeGrid: true, includeUnitList: true, includeModelPages: true });
  const toggleReportStatus = (key) => {
    setReportStatuses(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]);
  };

  useEffect(() => {
    projApi.getBuildings(project.id).then(r => {
      // v3.4.499 — مثل النسخة المكتبية: المباعة المنشورة لإعادة البيع تُعرض وتُحسب «متاح» (وبحجز مشترٍ معلق «محجوز») مع شطف أحمر
      const eff = (u) => (["sold", "sold_internal", "internal_sold"].includes(u.status) && u.resale_published)
        ? { ...u, orig_status: u.status, is_resale_pub: true, status: u.resale_pending_buyer ? "reserved" : "available" } : u;
      setBuildings((r.data.buildings || []).map(b => ({ ...b, units: (b.units || []).map(eff) })));
      setLoading(false);
    }).catch(() => setLoading(false));
    loadLastReport();
  }, [project.id]);

  const loadLastReport = () => {
    projApi.getProjectDocuments(project.id).then(r => {
      const docs = r.data.documents || [];
      const last = docs.filter(d => d.doc_type === "تقرير_توفر").sort((a,b) => new Date(b.created_at) - new Date(a.created_at))[0];
      if (last) {
        setLastReport(last);
        try {
          const notes = JSON.parse(last.notes || "{}");
          if (notes.hiddenUnitIds && Array.isArray(notes.hiddenUnitIds)) {
            setHiddenFromReport(new Set(notes.hiddenUnitIds));
          }
        } catch {}
      }
    }).catch(() => {});
  };

  const handleGenerateReport = async () => {
    if (generatingReport) return;
    if (reportStatuses.length === 0) { toast.error("اختر نوع واحد على الأقل"); return; }
    if (!reportSections.includeGrid && !reportSections.includeUnitList && !reportSections.includeModelPages) { toast.error("اختر قسم واحد على الأقل"); return; }
    setGeneratingReport(true);
    try {
      // v3.3.336 — قراءة الوحدات المحجوبة من القاعدة (المحفوظة في قسم "تحديد حجب الوحدات")
      let savedHidden = [];
      try {
        const projRes = await projApi.get(project.id);
        const rawUnits = projRes.data.units || [];
        const seen = new Set();
        const dedupedUnits = rawUnits.filter(u => { if (seen.has(u.id)) return false; seen.add(u.id); return true; });
        savedHidden = dedupedUnits
          .filter(u => u.hidden_in_marketing && (u.status === "available" || u.status === "available_sakani"))
          .map(u => u.id);
      } catch {}
      const columnVisibility = { showPrice: true, showArea: true, showRooms: true, showFacade: false };
      const r = await projApi.generateAvailabilityReport(project.id, reportStatuses, savedHidden, columnVisibility, reportSections);
      toast.success(`✅ تم إنشاء التقرير${savedHidden.length > 0 ? ` (${savedHidden.length} محجوبة)` : ""}`);
      setShowReportOptions(false);
      loadLastReport();
      // افتح التقرير الجديد مباشرة
      if (r.data && r.data.document && r.data.document.id) {
        setTimeout(() => {
          window.open(`/api/projects/${project.id}/availability-report/${r.data.document.id}/pdf`, "_blank");
        }, 500);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل إنشاء التقرير");
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleOpenLastReport = () => {
    if (!lastReport) return;
    const url = lastReport.drive_web_link || `/api/projects/${project.id}/availability-report/${lastReport.id}/pdf`;
    window.open(url, "_blank");
  };

  const allUnits = buildings.flatMap(b => (b.units || []).map(u => ({ ...u, buildingName: b.name })));
  const stats = {
    total: allUnits.length,
    available: allUnits.filter(u => u.status === "available" || u.status === "available_sakani").length, // v3.4.587
    sold: allUnits.filter(u => u.status === "sold").length,
    reserved: allUnits.filter(u => u.status === "reserved" || u.status === "reserved_sakani").length,
    soldInternal: allUnits.filter(u => u.status === "sold_internal").length,
  };

  const filteredBuildings = selectedBuilding === "all" ? buildings : buildings.filter(b => String(b.id) === selectedBuilding);

  const skBase = (s) => (s === "available_sakani" ? "available" : s === "reserved_sakani" ? "reserved" : s); // v3.4.587 — الحالتان السكنيتان بنفس الألوان
  const unitColor = (s) => {
    s = skBase(s);
    if (s === "available") return C.greenLight;
    if (!colorAll) return C.border; // رمادي لغير المتاح
    if (s === "sold") return C.red;
    if (s === "sold_internal") return "#7f1d1d";
    if (s === "reserved") return C.blue;
    if (s === "held") return C.yellow;
    if (s === "temporarily_blocked") return "#cbd5e1";
    return C.hint;
  };
  const unitTextColor = (s) => {
    s = skBase(s);
    if (s === "available") return C.greenDark;
    if (!colorAll) return C.hint;
    if (s === "sold") return C.redDark;
    if (s === "sold_internal") return "#fecaca";
    if (s === "reserved") return C.blueDark;
    if (s === "held") return C.yellowDark;
    if (s === "temporarily_blocked") return "#475569";
    return C.text;
  };
  const unitLabel = s => {
    if (s === "available_sakani") return "متاح (س)"; // v3.4.587
    if (s === "reserved_sakani") return "محجوز (س)";
    if (s === "available") return "متاح";
    if (s === "sold") return "مُباع";
    if (s === "sold_internal") return "داخلي";
    if (s === "reserved") return "محجوز";
    if (s === "held") return "مؤقت";
    if (s === "temporarily_blocked") return "غير متوفر";
    return s || "—";
  };

  // Toggle component
  const Toggle = ({ checked, onChange, label }) => (
    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 11, color: checked ? C.greenLight : C.sub }}>
      <div onClick={onChange} style={{
        width: 34, height: 18, borderRadius: 9, background: checked ? C.green : C.border,
        position: "relative", transition: "background 0.2s", cursor: "pointer",
      }}>
        <div style={{
          width: 14, height: 14, borderRadius: "50%", background: "#fff",
          position: "absolute", top: 2, transition: "right 0.2s, left 0.2s",
          ...(checked ? { left: 2 } : { left: 18 }),
        }} />
      </div>
      {label}
    </label>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      <MobileHeader title={project.name} subtitle={project.developer_name} onBack={onBack} />
      <div style={{ padding: "12px 16px", direction: "rtl" }}>
        {/* إحصائيات */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 }}>
          {[
            { label: "إجمالي", value: stats.total, color: C.text },
            { label: "متاح", value: stats.available, color: C.greenLight },
            { label: "مُباع", value: stats.sold, color: C.red },
            { label: "محجوز", value: stats.reserved, color: C.blue },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center", background: C.card, borderRadius: 8, padding: "8px 4px", border: `0.5px solid ${C.border}` }}>
              <div style={{ fontSize: 18, fontWeight: 500, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 9, color: C.sub }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* فلتر المباني */}
        <div style={{ display: "flex", gap: 6, marginBottom: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <span onClick={() => setSelectedBuilding("all")} style={{
            padding: "6px 16px", borderRadius: 20, fontSize: 12, cursor: "pointer", fontWeight: 500,
            background: selectedBuilding === "all" ? C.green : C.card, color: selectedBuilding === "all" ? "#fff" : C.sub,
            border: selectedBuilding === "all" ? "none" : `0.5px solid ${C.border}`,
          }}>الكل</span>
          {buildings.map(b => (
            <span key={b.id} onClick={() => setSelectedBuilding(String(b.id))} style={{
              padding: "6px 16px", borderRadius: 20, fontSize: 12, cursor: "pointer",
              background: selectedBuilding === String(b.id) ? C.green : C.card, color: selectedBuilding === String(b.id) ? "#fff" : C.sub,
              border: selectedBuilding === String(b.id) ? "none" : `0.5px solid ${C.border}`,
            }}>{b.name}</span>
          ))}
        </div>

        {/* خيارات العرض + زر إخفاء */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "stretch" }}>
          <div style={{ flex: 1, background: C.card, borderRadius: 10, padding: "10px 14px", border: `0.5px solid ${C.border}` }}>
            <div style={{ fontSize: 10, fontWeight: 500, color: C.hint, marginBottom: 8 }}>خيارات العرض</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
              <Toggle checked={showArea} onChange={() => setShowArea(!showArea)} label="المساحة" />
              <Toggle checked={showPrice} onChange={() => setShowPrice(!showPrice)} label="السعر" />
              <Toggle checked={showRooms} onChange={() => setShowRooms(!showRooms)} label="عدد الغرف" />
              <Toggle checked={showOfficialPrice} onChange={() => setShowOfficialPrice(!showOfficialPrice)} label="السعر الرسمي" />
              <Toggle checked={showSalePrice} onChange={() => setShowSalePrice(!showSalePrice)} label="سعر البيع" />
              <Toggle checked={showClientName} onChange={() => setShowClientName(!showClientName)} label="اسم العميل" />
              <Toggle checked={showMarketer} onChange={() => setShowMarketer(!showMarketer)} label="المسوق" />
              <Toggle checked={showFacade} onChange={() => setShowFacade(!showFacade)} label="الواجهة" />
            </div>
          </div>
          {/* زر إخفاء/إظهار المباع */}
          <div onClick={() => setOnlyAvailable(!onlyAvailable)} style={{
            width: 90, background: onlyAvailable ? "#052e16" : C.card, borderRadius: 10,
            border: `1.5px solid ${onlyAvailable ? C.greenLight : C.border}`,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            cursor: "pointer", padding: 8, transition: "all 0.2s",
          }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>{onlyAvailable ? "👁️" : "👁️‍🗨️"}</div>
            <div style={{ fontSize: 9, fontWeight: 600, color: onlyAvailable ? C.greenLight : C.sub, textAlign: "center", lineHeight: 1.3 }}>
              {onlyAvailable ? "المتاح فقط" : "إظهار الكل"}
            </div>
          </div>
        </div>

        {/* دليل الألوان */}
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 14, fontSize: 11 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4, color: C.sub }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: C.greenLight, display: "inline-block" }} /> متاح
          </span>
          {!onlyAvailable && <>
            <span style={{ display: "flex", alignItems: "center", gap: 4, color: C.sub }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: C.red, display: "inline-block" }} /> مُباع
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4, color: C.sub }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: C.blue, display: "inline-block" }} /> محجوز
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4, color: C.sub }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: C.yellow, display: "inline-block" }} /> مؤقت
            </span>
          </>}
        </div>

        {/* أزرار التقارير */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <button onClick={() => setShowReportOptions(!showReportOptions)} disabled={generatingReport}
            style={{
              flex: 1, padding: "12px 10px", background: showReportOptions ? "#0f5132" : C.green, border: "none", borderRadius: 10,
              color: "#fff", fontFamily: "inherit", fontSize: 13, fontWeight: 500,
              cursor: generatingReport ? "wait" : "pointer", opacity: generatingReport ? 0.6 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
            {generatingReport ? "⏳ جاري الإنشاء..." : (showReportOptions ? "✕ إلغاء" : "📊 إنشاء تحديث")}
          </button>
          <button onClick={handleOpenLastReport} disabled={!lastReport}
            style={{
              flex: 1, padding: "12px 10px", background: lastReport ? C.card : "#1a1a2e",
              border: `0.5px solid ${lastReport ? C.blue : C.border}`, borderRadius: 10,
              color: lastReport ? C.blue : C.hint, fontFamily: "inherit", fontSize: 13, fontWeight: 500,
              cursor: lastReport ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
            📄 {lastReport ? "فتح آخر تحديث" : "لا يوجد تحديث"}
          </button>
        </div>

        {/* v3.3.336 — لوحة خيارات التقرير */}
        {showReportOptions && (
          <div style={{ background: C.card, border: `1.5px solid ${C.green}`, borderRadius: 10, padding: 12, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.green, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              📋 خيارات التقرير
            </div>

            {/* Statuses */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 500, color: C.hint, marginBottom: 6 }}>اختر الوحدات المطلوب تضمينها:</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {[
                  { key: "available", label: "✅ المتاح", color: "#22c55e" },
                  { key: "resale", label: "🔄 إعادة البيع", color: "#f97316" }, // v3.4.153
                  { key: "sold", label: "🔴 المباع", color: "#ef4444" },
                  { key: "reserved", label: "🔵 المحجوز", color: "#3b82f6" },
                  { key: "held", label: "🟡 محجوز مؤقت", color: "#eab308" },
                ].map(s => (
                  <button key={s.key} onClick={() => toggleReportStatus(s.key)}
                    style={{ padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: reportStatuses.includes(s.key) ? 700 : 400,
                      cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                      background: reportStatuses.includes(s.key) ? s.color : "transparent",
                      border: `1.5px solid ${reportStatuses.includes(s.key) ? s.color : C.border}`,
                      color: reportStatuses.includes(s.key) ? "#fff" : C.sub }}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sections */}
            <div style={{ borderTop: `1px dashed ${C.border}`, paddingTop: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 500, color: C.hint, marginBottom: 6 }}>📑 أقسام التقرير المُضمَّنة:</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[
                  { key: "includeGrid", label: "🗺️ خريطة الوحدات" },
                  { key: "includeUnitList", label: "📋 جدول الوحدات" },
                  { key: "includeModelPages", label: "🏠 صفحات النماذج" },
                ].map(opt => (
                  <label key={opt.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 8, cursor: "pointer",
                    background: reportSections[opt.key] ? "rgba(34,197,94,0.1)" : "transparent",
                    border: `1px solid ${reportSections[opt.key] ? C.green : C.border}` }}>
                    <input type="checkbox" checked={reportSections[opt.key]}
                      onChange={() => setReportSections(prev => ({ ...prev, [opt.key]: !prev[opt.key] }))}
                      style={{ accentColor: C.green, cursor: "pointer", width: 14, height: 14 }} />
                    <span style={{ fontSize: 11, color: reportSections[opt.key] ? C.green : C.sub, fontWeight: reportSections[opt.key] ? 700 : 400 }}>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Generate button */}
            <button onClick={handleGenerateReport} disabled={generatingReport || reportStatuses.length === 0}
              style={{ width: "100%", padding: "10px", background: reportStatuses.length === 0 ? C.border : C.green,
                border: "none", borderRadius: 8, color: "#fff", fontSize: 12, fontWeight: 700,
                cursor: generatingReport || reportStatuses.length === 0 ? "not-allowed" : "pointer",
                fontFamily: "inherit", opacity: generatingReport ? 0.6 : 1 }}>
              {generatingReport ? "⏳ جاري الإنشاء..." : `📊 إنشاء التقرير (${reportStatuses.length} نوع)`}
            </button>
          </div>
        )}
        {lastReport && (
          <div style={{ fontSize: 10, color: C.hint, textAlign: "center", marginTop: -8, marginBottom: 14 }}>
            آخر تحديث: {new Date(lastReport.created_at).toLocaleString("ar-SA", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
          </div>
        )}


        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: C.sub }}>جاري التحميل...</div>
        ) : filteredBuildings.map(building => {
          const units = building.units || [];
          // تجميع كل الوحدات حسب الدور
          const floors = {};
          units.forEach(u => {
            const floor = u.floor_number || u.floor || "0";
            if (!floors[floor]) floors[floor] = [];
            floors[floor].push(u);
          });
          // لو الإخفاء مفعّل: نعرض فقط الأدوار اللي فيها متاح
          // لو الإخفاء معطّل: نعرض كل الأدوار
          const floorKeys = Object.keys(floors)
            .filter(f => onlyAvailable ? floors[f].some(u => (u.status === "available" || u.status === "available_sakani")) : true)
            .sort((a, b) => Number(b) - Number(a));
          const bAvail = units.filter(u => (u.status === "available" || u.status === "available_sakani")).length;
          const bSold = units.filter(u => u.status === "sold").length;

          return (
            <div key={building.id} style={{ background: C.card, borderRadius: 10, padding: 12, border: `0.5px solid ${C.border}`, marginBottom: 10 }}>
              {/* إحصائيات المبنى */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{building.name}</span>
                {!onlyAvailable && <>
                  <span style={{ fontSize: 11, color: C.sub }}>{units.length} وحدة</span>
                  <span style={{ fontSize: 11, color: C.greenLight }}>{bAvail} متاح</span>
                  <span style={{ fontSize: 11, color: C.red }}>{bSold} مباع</span>
                </>}
              </div>

              {floorKeys.length === 0 ? (
                <div style={{ textAlign: "center", padding: 12, color: C.hint, fontSize: 12 }}>لا توجد وحدات متاحة</div>
              ) : floorKeys.map(floor => {
                const hasExtra = showArea || showPrice || showRooms || showOfficialPrice || showSalePrice || showClientName || showMarketer || showFacade;
                return (
                  <div key={floor} style={{ display: "flex", alignItems: "stretch", gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 500, color: C.hint }}>الدور {floor}</div>
                    </div>
                    <div style={{ flex: 1, display: "grid", gridTemplateColumns: `repeat(${floors[floor].length}, 1fr)`, gap: 5 }}>
                      {floors[floor].map(u => {
                        const isAvailable = u.status === "available" || u.status === "available_sakani"; // v3.4.836 — «متاح سكني فقط» يُعامل متاحًا في التطبيق
                        const isHiddenFromReport = hiddenFromReport.has(u.id);
                        const treatAsUnavailable = !isAvailable || (onlyAvailable && isHiddenFromReport);
                        // v3.3.335 — render shared metadata block (works for both available & unavailable)
                        const renderMeta = (textColor) => (
                          <>
                            {showRooms && u.rooms != null && (
                              <div style={{ fontSize: 8, color: textColor, opacity: 0.85, marginTop: 1 }}>{u.rooms} غرف</div>
                            )}
                            {showArea && u.area_sqm && (
                              <div style={{ fontSize: 8, color: textColor, opacity: 0.85, marginTop: 1 }}>{u.area_sqm} م²</div>
                            )}
                            {showFacade && u.facade && (
                              <div style={{ fontSize: 8, color: textColor, opacity: 0.85, marginTop: 1 }}>🧭 {u.facade}</div>
                            )}
                            {showPrice && u.price && (
                              <div style={{ fontSize: 7, color: textColor, opacity: 0.85, marginTop: 1 }}>{fmt(u.price)}</div>
                            )}
                            {showOfficialPrice && u.price && (
                              <div style={{ fontSize: 7, color: textColor, opacity: 0.85, marginTop: 1 }}>📜 {fmt(u.price)}</div>
                            )}
                            {showSalePrice && u.sale_price && (
                              <div style={{ fontSize: 7, color: textColor, opacity: 0.85, marginTop: 1 }}>💵 {fmt(u.sale_price)}</div>
                            )}
                            {showClientName && u.client_name && (
                              <div style={{ fontSize: 7, color: textColor, opacity: 0.85, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={u.client_name}>
                                👤 {u.client_name.length > 12 ? u.client_name.substring(0, 12) + "…" : u.client_name}
                              </div>
                            )}
                            {showMarketer && u.marketer_name && (
                              <div style={{ fontSize: 7, color: textColor, opacity: 0.85, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={u.marketer_name}>
                                🧑‍💼 {u.marketer_name.length > 12 ? u.marketer_name.substring(0, 12) + "…" : u.marketer_name}
                              </div>
                            )}
                          </>
                        );
                        if (treatAsUnavailable) {
                          if (onlyAvailable) {
                            // إخفاء — فراغ شفاف يحافظ على التناسق
                            return <div key={u.id} style={{ borderRadius: 6, padding: hasExtra ? "6px 4px 4px" : "9px 4px" }} />;
                          }
                          // إظهار — ملوّنة حسب الحالة
                          return (
                            <div key={u.id} style={{
                              background: unitColor(u.status), borderRadius: 6,
                              padding: hasExtra ? "6px 4px 4px" : "9px 4px",
                              textAlign: "center", position: "relative", overflow: "hidden",
                            }}>
                              {u.is_resale_pub && <span title="منشورة لإعادة البيع" style={{ position: "absolute", top: 0, left: 0, width: 0, height: 0, borderTop: "11px solid #dc2626", borderRight: "11px solid transparent", borderTopLeftRadius: 6 }} />}
                              <div style={{ fontSize: 11, fontWeight: 500, color: unitTextColor(u.status) }}>
                                {u.unit_label || u.unit_code || u.unit_number}
                              </div>
                              {u.is_resale_pub && <div style={{ fontSize: 7.5, fontWeight: 700, color: unitTextColor(u.status), marginTop: 1 }}>🔄 إعادة بيع</div>}
                              {hasExtra && renderMeta(unitTextColor(u.status))}
                            </div>
                          );
                        }
                        return (
                          <div key={u.id} style={{
                            background: C.greenLight, borderRadius: 6,
                            padding: hasExtra ? "6px 4px 4px" : "9px 4px",
                            textAlign: "center", position: "relative", overflow: "hidden",
                          }}>
                            {u.is_resale_pub && <span title="منشورة لإعادة البيع" style={{ position: "absolute", top: 0, left: 0, width: 0, height: 0, borderTop: "11px solid #dc2626", borderRight: "11px solid transparent", borderTopLeftRadius: 6 }} />}
                            <div style={{ fontSize: 11, fontWeight: 500, color: C.greenDark }}>
                              {u.unit_label || u.unit_code || u.unit_number}
                            </div>
                            {u.is_resale_pub && <div style={{ fontSize: 7.5, fontWeight: 700, color: C.greenDark, marginTop: 1 }}>🔄 إعادة بيع</div>}
                            {hasExtra && renderMeta(C.greenDark)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// NewReservation — فورم حجز جديد
// ═══════════════════════════════════════════════════════════════
function NewReservation({ onBack, onSaved }) {
  const [options, setOptions] = useState({});
  const [clients, setClients] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [marketers, setMarketers] = useState([]);
  const [saving, setSaving] = useState(false);

  // sub-screens
  const [addScreen, setAddScreen] = useState(null); // "client" | "marketer" | "developer" | "unit"
  const [unitPickerData, setUnitPickerData] = useState(null);

  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    operation_type: "",
    client_ids: [],
    client_type: "",
    developer_ids: [],
    marketer_ids: [],
    unit_type: "",
    readiness: "",
    purchase_purpose: "",
    notes: "",
    value: "",
    dalala_amount: "",
    deposit_amount: "",
    deposit_status: "",
    bank_account: "",
    notes_extra: "",
    selected_unit_code: "",
    selected_project_id: null,
    selected_building_id: null,
    selected_floor: "",
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    Promise.all([
      refApi.getOptions(), refApi.getClients(), refApi.getDevelopers(), refApi.getMarketers(),
    ]).then(([o, c, d, m]) => {
      const opts = {};
      Object.entries(o.data.options).forEach(([k, arr]) => { opts[k] = arr.map(i => i.label); });
      setOptions(opts);
      setClients(c.data.items || []);
      setDevelopers(d.data.items || []);
      setMarketers(m.data.items || []);
    }).catch(() => {});
  }, []);

  // ─── Unit picker data load ───
  useEffect(() => {
    if (form.developer_ids.length > 0) {
      const dev = developers.find(d => d.id === form.developer_ids[0]?.id);
      if (dev?.linked_project_id) {
        projApi.getBuildings(dev.linked_project_id).then(r => {
          setUnitPickerData({ projectId: dev.linked_project_id, projectName: dev.name, buildings: r.data.buildings || [] });
        }).catch(() => {});
      } else {
        setUnitPickerData(null);
      }
    }
  }, [form.developer_ids, developers]);

  const handleSave = async () => {
    if (!form.operation_type) { toast.error("اختر نوع العملية"); return; }
    if (form.client_ids.length === 0) { toast.error("اختر العميل"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_ids: form.client_ids.map(c => c.id),
        developer_ids: form.developer_ids.map(d => d.id),
        marketer_ids: form.marketer_ids.map(m => m.id),
      };
      await resApi.create(payload);
      toast.success("تم إنشاء الحجز ✓");
      onSaved && onSaved();
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الحفظ");
    }
    setSaving(false);
  };

  // ─── Sub-screens ───
  if (addScreen === "client") {
    return <AddEntityScreen type="client" onBack={() => setAddScreen(null)} onAdded={(item) => { set("client_ids", [...form.client_ids, item]); setClients(prev => [...prev, item]); setAddScreen(null); }} />;
  }
  if (addScreen === "marketer") {
    return <AddEntityScreen type="marketer" onBack={() => setAddScreen(null)} onAdded={(item) => { set("marketer_ids", [...form.marketer_ids, item]); setMarketers(prev => [...prev, item]); setAddScreen(null); }} />;
  }
  if (addScreen === "developer") {
    return <AddEntityScreen type="developer" onBack={() => setAddScreen(null)} onAdded={(item) => { set("developer_ids", [...form.developer_ids, item]); setDevelopers(prev => [...prev, item]); setAddScreen(null); }} />;
  }
  if (addScreen === "unit" && unitPickerData) {
    return <UnitPickerScreen data={unitPickerData} onBack={() => setAddScreen(null)} onSelect={(code, buildingId, floor) => {
      set("selected_unit_code", code);
      set("selected_building_id", buildingId);
      set("selected_floor", floor);
      set("selected_project_id", unitPickerData.projectId);
      setAddScreen(null);
    }} />;
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column" }}>
      <MobileHeader title="حجز جديد" onBack={onBack} color={C.green} />

      <div style={{ flex: 1, padding: "16px", direction: "rtl", overflowY: "auto" }}>
        {/* ── القسم 1: البيانات الأساسية ── */}
        <div style={{ fontSize: 11, fontWeight: 500, color: C.hint, marginBottom: 10, display: "flex", alignItems: "center", gap: 4 }}>📋 البيانات الأساسية</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
          {/* التاريخ + نوع العملية */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelS}>التاريخ *</label>
              <input type="date" value={form.date} onChange={e => set("date", e.target.value)} style={inputS} />
            </div>
            <div>
              <label style={labelS}>نوع العملية *</label>
              <select value={form.operation_type} onChange={e => set("operation_type", e.target.value)} style={selectS}>
                <option value="">— اختر —</option>
                {(options.operation_type || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {/* العميل */}
          <div>
            <label style={labelS}>العميل *</label>
            <div style={{ display: "flex", gap: 8 }}>
              <select value={form.client_ids[0]?.id || ""} onChange={e => {
                const c = clients.find(c => c.id === parseInt(e.target.value));
                if (c) set("client_ids", [c]);
              }} style={{ ...selectS, flex: 1, color: form.client_ids.length > 0 ? C.text : C.sub }}>
                <option value="">ابحث عن العميل...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name} {c.phone ? `— ${c.phone}` : ""}</option>)}
              </select>
              <div style={btnAdd} onClick={() => setAddScreen("client")}><span style={{ color: C.green, fontSize: 20, fontWeight: 500 }}>+</span></div>
            </div>
          </div>

          {/* مصدر العميل */}
          <div>
            <label style={labelS}>مصدر العميل</label>
            <select value={form.client_type} onChange={e => set("client_type", e.target.value)} style={selectS}>
              <option value="">— اختر —</option>
              {(options.client_type || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          {/* المطور */}
          <div>
            <label style={labelS}>المطور / المشروع *</label>
            <div style={{ display: "flex", gap: 8 }}>
              <select value={form.developer_ids[0]?.id || ""} onChange={e => {
                const d = developers.find(d => d.id === parseInt(e.target.value));
                if (d) set("developer_ids", [d]);
              }} style={{ ...selectS, flex: 1, color: form.developer_ids.length > 0 ? C.text : C.sub }}>
                <option value="">اختر المطور...</option>
                {developers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <div style={btnAdd} onClick={() => setAddScreen("developer")}><span style={{ color: C.green, fontSize: 20, fontWeight: 500 }}>+</span></div>
            </div>
          </div>

          {/* الوحدة */}
          <div>
            <label style={labelS}>الوحدة</label>
            {form.selected_unit_code ? (
              <div style={{ background: C.greenDark, border: `1px solid ${C.greenLight}`, borderRadius: 8, padding: "10px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 10, color: "#86efac" }}>الوحدة المحددة</div>
                  <div style={{ fontSize: 16, fontWeight: 500, color: C.greenLight }}>{form.selected_unit_code}</div>
                </div>
                <span onClick={() => set("selected_unit_code", "")} style={{ color: C.red, fontSize: 14, cursor: "pointer" }}>✕</span>
              </div>
            ) : (
              <div onClick={() => unitPickerData && setAddScreen("unit")} style={{ ...inputS, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: unitPickerData ? "pointer" : "default", opacity: unitPickerData ? 1 : 0.5 }}>
                <span style={{ fontSize: 13, color: C.sub }}>{unitPickerData ? "اختر الوحدة من خريطة المشروع..." : "اختر المطور أولاً"}</span>
                <span style={{ color: C.green, fontSize: 12 }}>◀</span>
              </div>
            )}
          </div>

          {/* المسوق */}
          <div>
            <label style={labelS}>المسوق *</label>
            <div style={{ display: "flex", gap: 8 }}>
              <select value={form.marketer_ids[0]?.id || ""} onChange={e => {
                const m = marketers.find(m => m.id === parseInt(e.target.value));
                if (m) set("marketer_ids", [m]);
              }} style={{ ...selectS, flex: 1, color: form.marketer_ids.length > 0 ? C.text : C.sub }}>
                <option value="">ابحث عن المسوق...</option>
                {marketers.map(m => <option key={m.id} value={m.id}>{m.name} {m.phone ? `— ${m.phone}` : ""}</option>)}
              </select>
              <div style={btnAdd} onClick={() => setAddScreen("marketer")}><span style={{ color: C.green, fontSize: 20, fontWeight: 500 }}>+</span></div>
            </div>
          </div>

          {/* نوع الوحدة + الجاهزية + هدف الشراء */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelS}>نوع الوحدة</label>
              <select value={form.unit_type} onChange={e => set("unit_type", e.target.value)} style={{ ...selectS, padding: "11px 8px", fontSize: 12 }}>
                <option value="">— اختر —</option>
                {(options.unit_type || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={labelS}>الجاهزية</label>
              <select value={form.readiness} onChange={e => set("readiness", e.target.value)} style={{ ...selectS, padding: "11px 8px", fontSize: 12 }}>
                <option value="">— اختر —</option>
                {(options.readiness || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={labelS}>هدف الشراء</label>
              <select value={form.purchase_purpose} onChange={e => set("purchase_purpose", e.target.value)} style={{ ...selectS, padding: "11px 8px", fontSize: 12 }}>
                <option value="">— اختر —</option>
                {(options.purchase_purpose || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {/* ملاحظات */}
          <div>
            <label style={labelS}>ملاحظات</label>
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="ملاحظات..." rows={2} style={{ ...inputS, resize: "none" }} />
          </div>
        </div>

        {/* ── القسم 2: التفاصيل المالية ── */}
        <div style={{ height: 1, background: C.card, marginBottom: 16 }} />
        <div style={{ fontSize: 11, fontWeight: 500, color: C.yellow, marginBottom: 10, display: "flex", alignItems: "center", gap: 4 }}>💰 التفاصيل المالية</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelS}>سعر البيع (ر.س)</label>
              <input type="number" value={form.value} onChange={e => set("value", e.target.value)} placeholder="0" style={{ ...inputS, color: C.yellow, direction: "ltr", textAlign: "right", fontSize: 14 }} />
            </div>
            <div>
              <label style={labelS}>الدلالة (ر.س)</label>
              <input type="number" value={form.dalala_amount} onChange={e => set("dalala_amount", e.target.value)} placeholder="0" style={{ ...inputS, color: C.yellow, direction: "ltr", textAlign: "right", fontSize: 14 }} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div>
              <label style={labelS}>العربون (ر.س)</label>
              <input type="number" value={form.deposit_amount} onChange={e => set("deposit_amount", e.target.value)} placeholder="0" style={{ ...inputS, color: C.greenLight, direction: "ltr", textAlign: "right", fontSize: 13, padding: "11px 8px" }} />
            </div>
            <div>
              <label style={labelS}>حالة العربون</label>
              <select value={form.deposit_status} onChange={e => set("deposit_status", e.target.value)} style={{ ...selectS, padding: "11px 8px", fontSize: 12 }}>
                <option value="">— اختر —</option>
                {(options.deposit_status || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={labelS}>البنك</label>
              <select value={form.bank_account} onChange={e => set("bank_account", e.target.value)} style={{ ...selectS, padding: "11px 8px", fontSize: 12 }}>
                <option value="">— اختر —</option>
                {(options.bank_account || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={labelS}>ملاحظات إضافية</label>
            <textarea value={form.notes_extra} onChange={e => set("notes_extra", e.target.value)} placeholder="ملاحظات مالية أو إضافية..." rows={2} style={{ ...inputS, resize: "none" }} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: "14px 16px", borderTop: `0.5px solid ${C.card}`, background: C.bg }}>
        <div onClick={saving ? undefined : handleSave} style={{ background: saving ? C.hint : C.green, borderRadius: 10, padding: 14, textAlign: "center", cursor: saving ? "not-allowed" : "pointer" }}>
          <span style={{ fontSize: 16, fontWeight: 500, color: "#fff" }}>{saving ? "جاري الحفظ..." : "حفظ الحجز"}</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// UnitPickerScreen — اختيار الوحدة من خريطة المشروع
// ═══════════════════════════════════════════════════════════════
function UnitPickerScreen({ data, onBack, onSelect }) {
  const [selectedBuilding, setSelectedBuilding] = useState("all");
  const [selectedUnit, setSelectedUnit] = useState(null);

  const buildings = data.buildings || [];
  const filteredBuildings = selectedBuilding === "all" ? buildings : buildings.filter(b => String(b.id) === selectedBuilding);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column" }}>
      <MobileHeader title={`اختر وحدة — ${data.projectName}`} subtitle="الوحدات المتاحة فقط" onBack={onBack} />

      <div style={{ flex: 1, padding: "12px 16px", direction: "rtl", overflowY: "auto" }}>
        {/* فلتر المباني */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14, justifyContent: "center", flexWrap: "wrap" }}>
          <span onClick={() => setSelectedBuilding("all")} style={{
            padding: "6px 16px", borderRadius: 20, fontSize: 12, cursor: "pointer",
            background: selectedBuilding === "all" ? C.green : C.card, color: selectedBuilding === "all" ? "#fff" : C.sub,
            border: selectedBuilding === "all" ? "none" : `0.5px solid ${C.border}`,
          }}>الكل</span>
          {buildings.map(b => (
            <span key={b.id} onClick={() => setSelectedBuilding(String(b.id))} style={{
              padding: "6px 16px", borderRadius: 20, fontSize: 12, cursor: "pointer",
              background: selectedBuilding === String(b.id) ? C.green : C.card, color: selectedBuilding === String(b.id) ? "#fff" : C.sub,
              border: selectedBuilding === String(b.id) ? "none" : `0.5px solid ${C.border}`,
            }}>{b.name}</span>
          ))}
        </div>

        {filteredBuildings.map(building => {
          const units = building.units || [];
          const floors = {};
          units.forEach(u => {
            const floor = u.floor_number || u.floor || "0";
            if (!floors[floor]) floors[floor] = [];
            floors[floor].push(u);
          });
          const floorKeys = Object.keys(floors).sort((a, b) => Number(b) - Number(a));
          const availCount = units.filter(u => (u.status === "available" || u.status === "available_sakani")).length;

          return (
            <div key={building.id} style={{ background: C.card, borderRadius: 10, padding: 12, border: `0.5px solid ${C.border}`, marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.text, marginBottom: 10, textAlign: "center" }}>{building.name} — {availCount} متاح</div>
              {floorKeys.map(floor => (
                <div key={floor} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 44, textAlign: "center", flexShrink: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: C.hint }}>الدور {floor}</div>
                  </div>
                  <div style={{ flex: 1, display: "grid", gridTemplateColumns: `repeat(${floors[floor].length}, 1fr)`, gap: 6 }}>
                    {floors[floor].map(u => {
                      const available = u.status === "available" || u.status === "available_sakani"; // v3.4.836
                      const isSelected = selectedUnit?.id === u.id;
                      const code = u.unit_label || u.unit_code || u.unit_number;
                      return (
                        <div key={u.id} onClick={() => available && setSelectedUnit({ ...u, buildingId: building.id, buildingName: building.name, floor })}
                          style={{
                            background: isSelected ? C.greenDark : available ? C.greenLight : C.border,
                            borderRadius: 6, padding: "10px 4px", textAlign: "center",
                            cursor: available ? "pointer" : "default",
                            opacity: available ? 1 : 0.4,
                            border: isSelected ? `2px solid ${C.greenLight}` : "2px solid transparent",
                          }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: isSelected ? C.greenLight : available ? C.greenDark : C.hint }}>
                            {code} {isSelected ? "✓" : ""}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Footer — الوحدة المحددة */}
      {selectedUnit && (
        <div style={{ padding: "12px 16px", borderTop: `0.5px solid ${C.card}`, background: C.bg, direction: "rtl" }}>
          <div style={{ background: C.greenDark, border: `1px solid ${C.greenLight}`, borderRadius: 10, padding: "10px 16px", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 10, color: "#86efac" }}>الوحدة المحددة</div>
              <div style={{ fontSize: 20, fontWeight: 500, color: C.greenLight }}>{selectedUnit.unit_label || selectedUnit.unit_code || selectedUnit.unit_number}</div>
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 11, color: "#86efac" }}>{selectedUnit.buildingName} · الدور {selectedUnit.floor}</div>
              {selectedUnit.price && <div style={{ fontSize: 11, color: "#86efac" }}>{fmt(selectedUnit.price)} ر.س</div>}
            </div>
          </div>
          <div onClick={() => {
            const code = selectedUnit.unit_label || selectedUnit.unit_code || selectedUnit.unit_number;
            onSelect(code, selectedUnit.buildingId, selectedUnit.floor);
          }} style={{ background: C.green, borderRadius: 10, padding: 14, textAlign: "center", cursor: "pointer" }}>
            <span style={{ fontSize: 15, fontWeight: 500, color: "#fff" }}>تأكيد اختيار {selectedUnit.unit_label || selectedUnit.unit_code || selectedUnit.unit_number}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// AddEntityScreen — إضافة عميل / مسوق / مطور جديد
// ═══════════════════════════════════════════════════════════════
function AddEntityScreen({ type, onBack, onAdded }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", company: "", id_number: "", notes: "", marketer_classification: "", client_type: "", representative_name: "", representative_phone: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const config = {
    // v3.3.741 — حارس التكرار للعملاء والمسوقين
    client: { title: "عميل جديد", color: "#2563eb", addFn: (d) => addWithDupGuard(refApi.addClient, d) },
    marketer: { title: "مسوق جديد", color: "#a21caf", addFn: (d) => addWithDupGuard(refApi.addMarketer, d) },
    developer: { title: "مطور جديد", color: "#b45309", addFn: refApi.addDeveloper },
  }[type];

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("الاسم مطلوب"); return; }
    setSaving(true);
    try {
      const res = await config.addFn(form);
      const item = res.data.item || res.data;
      toast.success(`تم إضافة ${config.title.replace("جديد", "").trim()} ✓`);
      onAdded({ id: item.id, name: form.name, phone: form.phone });
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الإضافة");
    }
    setSaving(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column" }}>
      <MobileHeader title={config.title} onBack={onBack} color={config.color} />

      <div style={{ flex: 1, padding: "20px 16px", direction: "rtl", display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={labelS}>الاسم *</label>
          <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="الاسم الكامل" style={inputS} />
        </div>
        <div>
          <label style={labelS}>رقم الجوال</label>
          {/* v3.3.749 — إدخال جوال موحّد */}
          <PhoneInput value={form.phone} intl={form._phone_intl}
            onChange={v => set("phone", v)} onIntlChange={v => set("_phone_intl", v)} />
        </div>

        {type === "marketer" && (
          <div>
            <label style={labelS}>شركة المسوق</label>
            <input value={form.company} onChange={e => set("company", e.target.value)} placeholder="اسم الشركة أو المكتب" style={inputS} />
          </div>
        )}

        {(type === "client" || type === "marketer") && (
          <div>
            <label style={labelS}>رقم الهوية</label>
            <input value={form.id_number} onChange={e => set("id_number", e.target.value)} placeholder="رقم الهوية" style={{ ...inputS, direction: "ltr", textAlign: "right" }} />
          </div>
        )}

        {type === "developer" && (
          <>
            <div>
              <label style={labelS}>ممثل المطور</label>
              <input value={form.representative_name} onChange={e => set("representative_name", e.target.value)} placeholder="اسم الممثل" style={inputS} />
            </div>
            <div>
              <label style={labelS}>رقم الممثل</label>
              <input value={form.representative_phone} onChange={e => set("representative_phone", e.target.value)} placeholder="05XXXXXXXX" style={{ ...inputS, direction: "ltr", textAlign: "right" }} />
            </div>
          </>
        )}

        <div>
          <label style={labelS}>ملاحظات</label>
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="ملاحظات..." rows={2} style={{ ...inputS, resize: "none" }} />
        </div>
      </div>

      <div style={{ padding: "16px", borderTop: `0.5px solid ${C.card}` }}>
        <div onClick={saving ? undefined : handleSave} style={{ background: saving ? C.hint : config.color, borderRadius: 10, padding: 14, textAlign: "center", cursor: saving ? "not-allowed" : "pointer" }}>
          <span style={{ fontSize: 15, fontWeight: 500, color: "#fff" }}>{saving ? "جاري الحفظ..." : `حفظ واختيار ال${type === "client" ? "عميل" : type === "marketer" ? "مسوق" : "مطور"}`}</span>
        </div>
        <div style={{ fontSize: 10, color: C.hint, textAlign: "center", marginTop: 8 }}>سيتم الحفظ والاختيار تلقائياً في الحجز</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MobilePage — المكوّن الرئيسي
// ═══════════════════════════════════════════════════════════════
export default function MobilePage() {
  const { user, logout } = useAuth();
  const [screen, setScreen] = useState("home");
  const [statsFilter, setStatsFilter] = useState("month"); // "month" | "all"
  const [stats, setStats] = useState({ total: 0, completed: 0, failed: 0 });
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async (filter) => {
    setLoading(true);
    try {
      const params = { limit: 1 };
      if (filter === "month") {
        // v3.3.597 — use the ACTUAL last day of the month. Hard-coding "-31"
        // produced an invalid date (e.g. 2026-06-31) for 30-day months, which
        // Postgres rejected → the query errored and stats silently stayed 0.
        const now = new Date();
        const y = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
        params.date_from = `${y}-${mm}-01`;
        params.date_to = `${y}-${mm}-${String(lastDay).padStart(2, "0")}`;
      }
      const [allRes, completedRes, failedRes] = await Promise.all([
        resApi.list(params),
        resApi.list({ ...params, operation_status: "تمت العمليه" }),
        resApi.list({ ...params, operation_status: "لم تتم العمليه" }),
      ]);
      setStats({
        total: allRes.data.pagination?.total || allRes.data.total || 0,
        completed: completedRes.data.pagination?.total || completedRes.data.total || 0,
        failed: failedRes.data.pagination?.total || failedRes.data.total || 0,
      });
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadStats(statsFilter); }, [statsFilter, loadStats]);

  if (screen === "reservations") return <ReservationsList onBack={() => setScreen("home")} />;
  if (screen === "projects") return <ProjectsScreen onBack={() => setScreen("home")} />;
  if (screen === "new") return <NewReservation onBack={() => setScreen("home")} onSaved={() => loadStats(statsFilter)} />;

  return <HomeScreen onNavigate={setScreen} stats={stats} statsLoading={loading} user={user} statsFilter={statsFilter} setStatsFilter={setStatsFilter} />;
}
